import React, { useEffect, useRef } from "react";
import { IPostMessage, OnmoHtmlGame } from "../models/OnmoHtmlGame";
import {
  convertSeeds,
  freeMemoryAfterPlaying,
  loadGame,
  OnmoStorage,
  removeScript,
  resetStyleBody,
} from "../models/utils";
import { PAGE_CSS_STYLES } from "../models/constant";
import { GameType } from "@onmo/graphql/graphql";
import {
  GameStateType,
  ExternalGameMessage,
  HtmlGameMessage,
} from "../types/game";
import { HtmlGameContext } from "./HtmlGameProvider";

const oldBody = {
  margin: document?.body?.style?.margin,
  border: document?.body?.style?.border,
  overflow: document?.body?.style?.overflow,
  display: document?.body?.style?.display,
};

export const HtmlGameView = () => {
  const onmoController = useRef<OnmoHtmlGame>();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const HtmlGameCtx = React.useContext(HtmlGameContext);
  const gameData = HtmlGameCtx?.gameData;
  const callbacks = HtmlGameCtx?.callbacks;
  const setOnmoHtmlGame = HtmlGameCtx?.setController;

  useEffect(() => {
    if (!gameData) return;

    const keysBeforeJSON = OnmoStorage.getHtmlKeys();
    const keysBefore =
      keysBeforeJSON === null
        ? Object.keys(window)
        : JSON.parse(keysBeforeJSON);
    if (keysBeforeJSON === null) {
      OnmoStorage.setHtmlKeys(JSON.stringify(keysBefore));
    }

    const onmoHtmlGame = new OnmoHtmlGame();

    // Add postMessage function to send messages to iframe
    onmoHtmlGame.postMessageToFrame = (message: IPostMessage) => {
      if (
        gameData.type === GameType.IframeHtml &&
        iframeRef.current?.contentWindow
      ) {
        iframeRef.current.contentWindow.postMessage(message, "*");
        console.log("Message sent to iframe:", message);
      } else {
        console.warn(
          "Cannot send message: iframe not available or not external game",
        );
      }
    };

    globalThis.onmoHtmlGame = onmoHtmlGame;
    setOnmoHtmlGame?.(onmoHtmlGame);
    if (gameData.type === GameType.IframeHtml) return;
    const { onBundleLoadingProgress } = callbacks || {};
    onmoHtmlGame.seeds = convertSeeds(gameData.seeds) || 0;
    if (gameData.type === GameType.DirectHtml) {
      onmoHtmlGame.isFullGameMode = true;
      onmoHtmlGame.listMomentData = gameData.listMomentData;
      onmoHtmlGame.momentDatas = "{}";
    } else {
      onmoHtmlGame.momentDatas = gameData.momentDatas;
    }
    onmoController.current = onmoHtmlGame;

    const xhr = new XMLHttpRequest();
    const script = document.createElement("script");
    loadGame(xhr, script, gameData.bundleURL, onBundleLoadingProgress);

    if (callbacks) {
      const { onEndGame, onHtmlGameLoaded, onScore } = callbacks;

      const endGameListener = (data: [{ [key: string]: number }]) => {
        onEndGame(onmoHtmlGame, data?.[0]?.score);
      };

      const dataListener = (data: [{ [key: string]: number }]) => {
        onScore(onmoHtmlGame, data?.[0]?.score);
      };

      const unloadedListener = () => {
        // listener unloaded
      };

      const loadedListener = () => {
        onHtmlGameLoaded(onmoHtmlGame);
      };

      const loadingListener = () => {};

      const domListener = () => {};

      onmoHtmlGame.addListener("END_GAME", endGameListener);
      onmoHtmlGame.addListener("DATA", dataListener);
      onmoHtmlGame.addListener("UNLOADED", unloadedListener);
      onmoHtmlGame.addListener("LOADED", loadedListener);
      onmoHtmlGame.addListener("LOADING", loadingListener);
      onmoHtmlGame.addListener("DOM", domListener);
    }

    return () => {
      try {
        removeScript(script);
        xhr.abort();
        onmoHtmlGame?.removeAllListeners();
        onmoHtmlGame?.hideCanvas(true);
        onmoHtmlGame?.soundOff();
        onmoHtmlGame?.unload();
        setOnmoHtmlGame?.();
        globalThis.onmoHtmlGame = null;
        resetStyleBody(oldBody);
        freeMemoryAfterPlaying(keysBefore);
      } catch (error) {
        console.error("error at htmlGameView", error);
      }
    };
  }, [callbacks, !!gameData, setOnmoHtmlGame]);

  useEffect(() => {
    if (!gameData || gameData?.type === GameType.DirectHtml) return;
    const styles = PAGE_CSS_STYLES;

    for (const key in styles.body) {
      if (Object.prototype.hasOwnProperty.call(styles.body, key)) {
        // @ts-expect-error
        document.body.style[key] = styles.body[key];
      }
    }
    for (const key in styles.html) {
      if (Object.prototype.hasOwnProperty.call(styles.html, key)) {
        // @ts-expect-error
        document.documentElement.style[key] = styles.html[key];
      }
    }
    return () => {
      for (const key in styles.body) {
        if (Object.prototype.hasOwnProperty.call(styles.body, key)) {
          // @ts-expect-error
          document.body.style[key] = null;
        }
      }
      for (const key in styles.html) {
        if (Object.prototype.hasOwnProperty.call(styles.html, key)) {
          // @ts-expect-error
          document.documentElement.style[key] = null;
        }
      }
    };
  }, [gameData]);
  useEffect(() => {
    if (gameData?.type === "moment") return;
    if (gameData?.type === GameType.DirectHtml) return;
    const handleIframeMessage = (event: MessageEvent) => {
      if (
        event?.data?.action === "showad" &&
        event?.data?.payload?.type === "rewarded"
      ) {
        callbacks?.onClickRevive?.(event.data as ExternalGameMessage);
      } else if (event?.data?.gamestate === GameStateType.Loaded) {
        callbacks?.onHtmlGameLoaded?.(onmoController.current!);
      } else if (event?.data?.gamestate === GameStateType.AttemptOver) {
        const dataMessage = event.data as HtmlGameMessage;
        callbacks?.onEndGame?.(onmoController.current!, dataMessage.score);
      } else if (
        event?.data?.gamestate === GameStateType.AttemptOver ||
        event?.data?.gamestate === GameStateType.LevelUp
      ) {
        const dataMessage = event.data as HtmlGameMessage;
        callbacks?.onScore?.(onmoController.current!, dataMessage.score);
      }
    };

    window.addEventListener("message", handleIframeMessage);
    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  }, [gameData, callbacks]);

  if (gameData?.type === GameType.IframeHtml) {
    return (
      <iframe
        ref={iframeRef}
        src={gameData.url}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
        }}
        title="External Game"
      />
    );
  }

  return <div id="error_log" style={{ display: "none" }} />;
};
